import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, 
  Heart,
  Bell,
  BellOff,
  ArrowRight,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FollowedTeams = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [followedTeams, setFollowedTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchFollowedTeams();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchFollowedTeams = async () => {
    try {
      const response = await axios.get(`${API}/following/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowedTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch followed teams:', error);
      toast.error('Failed to load followed teams');
    } finally {
      setLoading(false);
    }
  };

  const handleUnfollow = async (teamId) => {
    try {
      await axios.delete(`${API}/teams/${teamId}/follow`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowedTeams(prev => prev.filter(t => t.team_id !== teamId));
      toast.success('Unfollowed team');
    } catch (error) {
      toast.error('Failed to unfollow team');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="text-center py-12">
          <CardContent>
            <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">Login Required</h3>
            <p className="text-muted-foreground mb-6">
              Please login to see your followed teams
            </p>
            <Link to="/login">
              <Button className="btn-primary">Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="followed-teams-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-black flex items-center gap-3">
          <Heart className="w-8 h-8 text-red-500 fill-red-500" />
          <span className="text-primary">FOLLOWED TEAMS</span>
        </h1>
        <p className="text-muted-foreground mt-2">
          Teams you follow. You will receive push notifications when they play.
        </p>
      </div>

      {/* Info Card */}
      <Card className="mb-6 bg-primary/5 border-primary/20">
        <CardContent className="p-4 flex items-start gap-3">
          <Bell className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <p className="font-medium">Spectator Mode Active</p>
            <p className="text-sm text-muted-foreground">
              You will receive push notifications 1 hour before any game involving your followed teams.
              Make sure you have enabled notifications in your browser.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Followed Teams List */}
      {followedTeams.length > 0 ? (
        <div className="space-y-4">
          {followedTeams.map((follow) => (
            <Card key={follow.id} className="card-hover" data-testid={`followed-team-${follow.team_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{follow.team_name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary">
                          {follow.team_age_category} • {follow.team_gender}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          Following since {new Date(follow.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Link to={`/teams/${follow.team_id}`}>
                      <Button variant="outline" size="sm" className="gap-1">
                        View
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleUnfollow(follow.team_id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <BellOff className="w-4 h-4 mr-1" />
                      Unfollow
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <CardContent>
            <Heart className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">No Followed Teams</h3>
            <p className="text-muted-foreground mb-6">
              Follow teams to get notifications about their games.
              Browse the teams page to find teams to follow.
            </p>
            <Link to="/teams">
              <Button className="btn-primary gap-2">
                <Users className="w-4 h-4" />
                Browse Teams
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
